import type {
  SearchActionPlansQuery,
  SearchBuildingFragment,
  SearchEconomicUnitsQuery,
  SearchPortfoliosQuery,
  SearchScenariosQuery,
} from '@predium/client-graphql';
import { t } from '@i18n';
import { org_sector_type_enum } from '@predium/enums';
import isNil from 'lodash/isNil';
import { NavigateFunction } from 'react-router-dom';
import { Actor } from 'xstate';
import { ICONS, NAVIGATION_ICONS } from '@/assets/icons';
import { GlobalSearchConfigProps } from '@/components/global-search/GlobalSearchConfig';
import { globalSearchMachine } from '@/components/global-search/machines/globalSearchMachine';
import {
  ActionPlanItem,
  BuildingIdItem,
  BuildingItem,
  EconomicUnitItem,
  NavigationItem,
  NavItem,
  PortfolioItem,
  ScenarioItem,
  SearchResultItem,
} from '@/components/global-search/type';
import { TrackEventType } from '@/hooks/usePosthogTracking';
import { PATHS } from '@/routes';

export function getAllNavigationItems(config: GlobalSearchConfigProps): NavigationItem[] {
  return config.map((option: NavItem) => {
    return {
      title: option.title,
      path: option.path,
      type: 'navigation',
      icon: option.icon,
    };
  });
}

function getDriveNavigationOptions(folderId: number | undefined) {
  if (isNil(folderId)) return [];
  return [{ path: PATHS.drive.folder({ id: folderId }), icon: ICONS.PH_FOLDER, label: t('GlobalSearch_GoToDrive') }];
}

/**
 * Converts building search results to searchable items with quick actions
 * @param buildings Array of building fragments from GraphQL
 * @returns Array of building items formatted for search
 */
export function getBuildingItems(buildings: SearchBuildingFragment[]): BuildingItem[] {
  if (!buildings) return [];
  return buildings.map((building) => {
    const navigationOptions = [
      ...getDriveNavigationOptions(building.folder?.id),
      {
        path: PATHS.dataCollection.building({ id: building.id }),
        icon: ICONS.DATABASE_OUTLINE,
        label: t('GlobalSearch_GoToDataCollection'),
      },
      {
        path: PATHS.statusQuoAnalysis.building({ id: building.id }),
        icon: ICONS.CHART_BAR,
        label: t('GlobalSearch_GoToStatusQuo'),
      },
    ];

    return {
      title: building.address.street,
      path: PATHS.dataCollection.building({ id: building.id }),
      displayBuildingPath: `${building.address.postal_code} ${building.address.city}`,
      customerExternalIdentifier: building.customer_external_identifier,
      type: 'building',
      buildingId: building.id,
      icon: ICONS.HOME,
      navigationOptions,
    };
  });
}

export function getBuildingIdItems(buildings: SearchBuildingFragment[]): BuildingIdItem[] {
  return getBuildingItems(buildings).map(
    (building): BuildingIdItem => ({
      ...building,
      type: 'building-id',
      title: `${building.title} (${t('GlobalSearch_PrediumIdSuffix', { id: building.buildingId })})`,
    }),
  );
}

export function getActionPlanItems(actionPlans?: SearchActionPlansQuery['action_plan']): ActionPlanItem[] {
  if (!actionPlans) return [];
  return actionPlans.map((actionPlan) => {
    const buildingAddress = `${actionPlan.building.address.street}, ${actionPlan.building.address.postal_code} ${actionPlan.building.address.city}`;
    const scenarioName = actionPlan.scenario_version.scenario.name;
    return {
      title: `Action Plan: ${scenarioName}`,
      path: PATHS.actionPlanning.actionPlan({
        scenarioId: actionPlan.scenario_version.scenario.id.toString(),
        stableId: actionPlan.stable_id.toString(),
      }),
      buildingAddress,
      scenarioName,
      customerExternalIdentifier: actionPlan.building.customer_external_identifier,
      type: 'action-plan',
      icon: NAVIGATION_ICONS.ACTION_PLANNING,
    };
  });
}

export function getScenarioItems(scenarios?: SearchScenariosQuery['scenario']): ScenarioItem[] {
  if (!scenarios) return [];
  return scenarios.map((scenario) => ({
    title: scenario.name,
    path: PATHS.actionPlanning.scenario({ scenarioId: scenario.id.toString() }),
    id: scenario.id,
    type: 'scenario',
    icon: NAVIGATION_ICONS.ACTION_PLANNING,
  }));
}

export function getPortfolioItems(portfolios: SearchPortfoliosQuery['portfolio'] | undefined): PortfolioItem[] {
  if (!portfolios) return [];
  return portfolios.map((portfolio) => ({
    title: `Status Quo Analysis: ${portfolio.name}`,
    path: PATHS.statusQuoAnalysis.portfolio({ portfolioIds: [portfolio.id] }),
    id: portfolio.id,
    type: 'portfolio',
    icon: NAVIGATION_ICONS.PORTFOLIO_ANALYSIS,
    navigationOptions: [
      ...getDriveNavigationOptions(portfolio.folder?.id),
      {
        path: PATHS.statusQuoAnalysis.portfolio({ portfolioIds: [portfolio.id] }),
        icon: ICONS.CHART_BAR,
        label: t('GlobalSearch_GoToStatusQuo'),
      },
    ],
  }));
}

export function getEconomicUnitItems(
  economicUnits: SearchEconomicUnitsQuery['economic_unit'] | undefined,
): EconomicUnitItem[] {
  if (!economicUnits) return [];
  return economicUnits.map((economicUnit) => ({
    title: `Status Quo Analysis: ${economicUnit.name}`,
    path: PATHS.statusQuoAnalysis.economicUnits({ economicUnitIds: [economicUnit.id] }),
    id: economicUnit.id,
    type: 'economic-unit',
    icon: NAVIGATION_ICONS.ECONOMIC_UNITS,
    navigationOptions: [
      ...getDriveNavigationOptions(economicUnit.folder?.id),
      {
        path: PATHS.statusQuoAnalysis.economicUnits({ economicUnitIds: [economicUnit.id] }),
        icon: ICONS.CHART_BAR,
        label: t('GlobalSearch_GoToStatusQuo'),
      },
    ],
  }));
}

/**
 * Filters search items (all types) based on search query
 * @param inputData Array of search items to filter
 * @param query Search query string
 * @returns Filtered array of search items
 */
export function applyFilter({
  inputData,
  query,
}: {
  inputData: SearchResultItem[];
  query: string;
}): SearchResultItem[] {
  if (!query) return inputData;

  const lowerQuery = query.toLowerCase();

  return inputData.filter((item) => {
    const titleMatch = item.title.toLowerCase().includes(lowerQuery);

    switch (item.type) {
      case 'navigation':
        return titleMatch;

      case 'building':
        return (
          titleMatch ||
          item.displayBuildingPath.toLowerCase().includes(lowerQuery) ||
          (item.customerExternalIdentifier && item.customerExternalIdentifier.toLowerCase().includes(lowerQuery))
        );

      case 'building-id':
        return String(item.buildingId).includes(lowerQuery);

      case 'action-plan':
        return (
          titleMatch ||
          item.buildingAddress.toLowerCase().includes(lowerQuery) ||
          item.scenarioName.toLowerCase().includes(lowerQuery) ||
          (item.customerExternalIdentifier && item.customerExternalIdentifier.toLowerCase().includes(lowerQuery))
        );

      case 'scenario':
      case 'portfolio':
      case 'economic-unit':
        return titleMatch;

      default:
        return titleMatch;
    }
  });
}

type GroupsProps = {
  [key: string]: SearchResultItem[];
};

const resultTypeOrder: SearchResultItem['type'][] = [
  'all',
  'building',
  'building-id',
  'economic-unit',
  'scenario',
  'action-plan',
  'portfolio',
  'navigation',
];

/**
 * Gets the sort order index for item types
 */
function getTypeSortOrder(type: SearchResultItem['type']): number {
  const index = resultTypeOrder.indexOf(type);
  return index === -1 ? 999 : index;
}

/**
 * Sorts items by type to maintain grouping for keyboard navigation
 * Returns a flat array sorted by type order
 */
export function sortItemsByType(items: SearchResultItem[]): SearchResultItem[] {
  return [...items].sort((a, b) => {
    const orderA = getTypeSortOrder(a.type);
    const orderB = getTypeSortOrder(b.type);
    return orderA - orderB;
  });
}

/**
 * Groups search items by their type property, with buildings always appearing first
 * @param array Array of search items to group
 * @returns Object with group names as keys and arrays of items as values
 */
export function groupedData(array: SearchResultItem[]): GroupsProps {
  return array.reduce((groups: GroupsProps, item) => {
    const groupName = item.type;
    groups[groupName] = groups[groupName] || [];
    groups[groupName].push(item);
    return groups;
  }, {} as GroupsProps);
}

/**
 * Gets the sort order for groups with specific ordering
 * @param groups Object with grouped search results
 * @returns Sorted array of group keys
 */
export function getSortedGroupKeys(groups: GroupsProps): string[] {
  const groupOrder: SearchResultItem['type'][] = [
    'building',
    'building-id',
    'economic-unit',
    'scenario',
    'action-plan',
    'portfolio',
    'navigation',
  ];

  return (Object.keys(groups) as SearchResultItem['type'][]).sort((a, b) => {
    const indexA = groupOrder.indexOf(a);
    const indexB = groupOrder.indexOf(b);

    // If both groups are in the predefined order, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the predefined order, it comes first
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // Otherwise, sort alphabetically
    return a.localeCompare(b);
  });
}

/**
 * Get translated group label for search results based on type
 */
export function getGroupLabel(type: SearchResultItem['type'], orgSector: org_sector_type_enum): string {
  switch (type) {
    case 'all':
      return t('GlobalSearch_All');
    case 'building':
      return t('GlobalSearch_Buildings');
    case 'building-id':
      return t('GlobalSearch_IDs');
    case 'action-plan':
      return t('GlobalSearch_ActionPlans');
    case 'scenario':
      return t('GlobalSearch_Scenarios');
    case 'portfolio':
      return t('GlobalSearch_Portfolios');
    case 'economic-unit':
      return t('GlobalSearch_EconomicUnits', { orgSector });
    case 'navigation':
      return t('GlobalSearch_Navigation');
    default:
      return '';
  }
}

/**
 * Sort types array by predefined order
 */
function sortTypesByOrder(types: SearchResultItem['type'][]): SearchResultItem['type'][] {
  return [...types].sort((a, b) => {
    const indexA = resultTypeOrder.indexOf(a);
    const indexB = resultTypeOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });
}

/**
 * Get available types from filtered data, sorted in the same order as displayed
 */
export function getAvailableTypes(dataFiltered: SearchResultItem[]): SearchResultItem['type'][] {
  const types = new Set<SearchResultItem['type']>();
  dataFiltered.forEach((item) => {
    types.add(item.type);
  });

  const TypesArray = Array.from(types);
  const sortedTypes = sortTypesByOrder(TypesArray);

  if (sortedTypes.length > 0) {
    sortedTypes.unshift('all');
  }
  return sortedTypes;
}

/**
 * Get secondary content for search result items (without highlighting)
 */
export function getSecondaryContent(item: SearchResultItem, orgSector: org_sector_type_enum): string | undefined {
  switch (item.type) {
    case 'building':
    case 'building-id':
      return item.displayBuildingPath;
    case 'action-plan':
      return item.buildingAddress;
    case 'portfolio':
      return t('GlobalSearch_Portfolios');
    case 'economic-unit':
      return t('GlobalSearch_EconomicUnits', { orgSector });
    case 'scenario':
      return t('GlobalSearch_Scenarios');
    case 'navigation':
      return t('GlobalSearch_Navigation');
    default:
      return undefined;
  }
}

function getPlatform() {
  const nav = window.navigator as Navigator & { userAgentData?: { platform?: string } };
  return (
    nav.userAgentData?.platform?.toLowerCase() ||
    window.navigator.userAgent.toLowerCase() ||
    window.navigator.platform.toLowerCase()
  );
}

export function getShortcutLabel() {
  const platform = getPlatform();

  // Check for Mac platforms
  if (platform.includes('mac')) return '⌘K';
  // For all other platforms (Windows, Linux, etc.)
  return 'Ctrl+K';
}

export const globalSearchHotkeyHandlers =
  ({
    isOpen,
    send,
    trackEvent,
    navigate,
  }: {
    isOpen: boolean;
    send: Actor<typeof globalSearchMachine>['send'];
    trackEvent: TrackEventType;
    navigate: NavigateFunction;
  }) =>
  (event: KeyboardEvent) => {
    const keyHandlers: Record<string, () => void> = {
      k: () => {
        const isMac = getPlatform().includes('mac');
        const isPressingSuperKey = (isMac && event.metaKey) || (!isMac && event.ctrlKey);
        if (!isOpen && isPressingSuperKey) {
          event.preventDefault();
          trackEvent('GLOBAL_SEARCH_OPENED');
          send({ type: 'OPEN' });
          return;
        }
        if (isOpen && isPressingSuperKey) {
          event.preventDefault();
          send({ type: 'CLOSE' });
        }
      },
      ArrowLeft: () => {
        if (!isOpen) return;
        event.preventDefault();
        send({ type: 'LEFT' });
      },
      ArrowRight: () => {
        if (!isOpen) return;
        event.preventDefault();
        send({ type: 'RIGHT' });
      },
      ArrowUp: () => {
        if (!isOpen) return;
        event.preventDefault();
        send({ type: 'UP' });
      },
      ArrowDown: () => {
        if (!isOpen) return;
        event.preventDefault();
        send({ type: 'DOWN' });
      },
      Tab: () => {
        if (!isOpen) return;
        event.preventDefault();
        send({ type: 'TAB' });
      },
      Enter: () => {
        if (!isOpen) return;
        event.preventDefault();
        send({ type: 'SELECT_ITEM', trackEvent, navigate });
      },
      Escape: () => {
        if (!isOpen) return;
        event.preventDefault();
        send({ type: 'CLOSE' });
      },
    };

    return keyHandlers[event.key];
  };

export const getSelectedResults = ({
  results,
  selectedTabType,
}: {
  results: SearchResultItem[];
  selectedTabType: SearchResultItem['type'];
}) => {
  if (selectedTabType === 'all') return results;
  return results.filter((result) => result.type === selectedTabType);
};
